import { poserAnnonce } from "../annonce.js";
import { poserProfil, type Profil, type Sanction } from "../hoa/profil.js";
import { empreintePhoto, photoCourante, poserPhoto } from "../hoa/photo.js";
import { signMessage } from "@hoa/crypto";
import { createLocalAccount, enregistrerEmail, loadAccount, setPseudo, type LocalAccount } from "./account.js";

/**
 * Le compte côté serveur : e-mail, mot de passe, clé de secours.
 *
 * Le téléphone garde sa propre clé (celle qui signe les reçus) ; c'est elle
 * que le serveur relie au compte. Chaque demande est signée sur un défi à
 * usage unique : aucun jeton de session n'est gardé dans le navigateur.
 * Aucun e-mail n'est envoyé (décision du 29 septembre).
 */

export interface CompteVisible {
  email: string;
  pseudo: string;
}

async function poster<T>(chemin: string, corps: unknown): Promise<T> {
  const r = await fetch(chemin, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(corps),
  });
  const j = (await r.json().catch(() => ({}))) as T & { error?: string };
  if (!r.ok) throw new Error(j.error ?? `Erreur ${r.status}`);
  return j;
}

/** Signe `[action, défi, ...champs]` avec la clé du téléphone. */
async function signe(compte: LocalAccount, action: string, ...champs: unknown[]): Promise<Record<string, string>> {
  const r = await fetch("/api/nonce");
  const { nonce } = (await r.json()) as { nonce: string };
  return {
    pubkey: compte.account.publicKeyHex,
    nonce,
    signature: signMessage(compte.account, JSON.stringify([action, nonce, ...champs])),
  };
}

/** La clé du téléphone : celle qui existe, ou une nouvelle, fabriquée sans rien demander. */
async function cleDuTelephone(courant: LocalAccount | null, pseudo: string): Promise<LocalAccount> {
  return courant ?? (await createLocalAccount(pseudo));
}

const emailSigne = (e: string) => e.trim().toLowerCase();

async function relie(compte: CompteVisible, pseudoLocal: string): Promise<LocalAccount> {
  await enregistrerEmail(compte.email);
  if (compte.pseudo && !pseudoLocal) await setPseudo(compte.pseudo);
  return (await loadAccount())!;
}

export async function creerCompte(
  courant: LocalAccount | null,
  e: { email: string; motDePasse: string; pseudo: string },
): Promise<{ compte: LocalAccount; cleSecours: string }> {
  const tel = await cleDuTelephone(courant, e.pseudo);
  const r = await poster<{ compte: CompteVisible; cleSecours: string }>("/api/comptes", {
    ...(await signe(tel, "creer-compte", emailSigne(e.email))),
    email: e.email,
    motDePasse: e.motDePasse,
    pseudo: e.pseudo,
  });
  return { compte: await relie(r.compte, e.pseudo), cleSecours: r.cleSecours };
}

export async function connecterCompte(
  courant: LocalAccount | null,
  e: { email: string; motDePasse: string; pseudoLocal: string },
): Promise<{ compte: LocalAccount; pseudo: string }> {
  const tel = await cleDuTelephone(courant, e.pseudoLocal);
  const r = await poster<{ compte: CompteVisible }>("/api/comptes/connexion", {
    ...(await signe(tel, "connexion", emailSigne(e.email))),
    email: e.email,
    motDePasse: e.motDePasse,
  });
  return { compte: await relie(r.compte, e.pseudoLocal), pseudo: r.compte.pseudo };
}

export async function secoursCompte(
  courant: LocalAccount | null,
  e: { email: string; cleSecours: string; motDePasse: string; pseudoLocal: string },
): Promise<{ compte: LocalAccount; cleSecours: string; pseudo: string }> {
  const tel = await cleDuTelephone(courant, e.pseudoLocal);
  const r = await poster<{ compte: CompteVisible; cleSecours: string }>("/api/comptes/secours", {
    ...(await signe(tel, "secours", emailSigne(e.email))),
    email: e.email,
    cleSecours: e.cleSecours,
    motDePasse: e.motDePasse,
  });
  return { compte: await relie(r.compte, e.pseudoLocal), cleSecours: r.cleSecours, pseudo: r.compte.pseudo };
}

/**
 * Le compte existe-t-il toujours pour ce téléphone ? `null` s'il a été
 * supprimé ou délié ailleurs ; `undefined` si le serveur ne répond pas (on
 * ne change rien).
 */
export async function verifierCompte(courant: LocalAccount): Promise<CompteVisible | null | undefined> {
  try {
    const r = await poster<{
      compte: CompteVisible | null;
      profil?: Profil | null;
      avertissements?: string[];
      sanction?: Sanction | null;
      photo?: string | null;
      photoRetiree?: boolean;
    }>("/api/comptes/moi", await signe(courant, "moi"));
    poserProfil(r.profil ?? null, r.sanction ?? null);
    // La photo : le compte fait foi. Une photo retirée par la régie
    // disparaît aussi d'ici ; une photo prise avant le compte y monte.
    if (r.compte) {
      if (r.photo) poserPhoto(r.photo);
      else if (r.photoRetiree) poserPhoto(null);
      else if (photoCourante()) void photoDuCompte(courant, photoCourante()).catch(() => undefined);
    }
    // Un avertissement est dit une fois, dans la bulle de Hoa. Une suspension
    // a son propre écran (H-Suspendu).
    const dits = (r.avertissements ?? []).map((m) => `Un avertissement de la régie : ${m}`);
    if (dits.length > 0) poserAnnonce(dits.join(" "), "mefiant");
    return r.compte;
  } catch {
    return undefined;
  }
}

export async function pseudoDuCompte(courant: LocalAccount, pseudo: string): Promise<void> {
  try {
    await poster("/api/comptes/pseudo", { ...(await signe(courant, "pseudo", pseudo)), pseudo });
  } catch {
    /* le pseudo local reste ; le serveur le reprendra au prochain changement */
  }
}

export async function deconnecterCompte(courant: LocalAccount): Promise<LocalAccount> {
  await poster("/api/comptes/deconnexion", await signe(courant, "deconnexion"));
  await enregistrerEmail(null);
  return (await loadAccount())!;
}

export async function supprimerCompte(courant: LocalAccount, motDePasse: string): Promise<LocalAccount> {
  await poster("/api/comptes/suppression", { ...(await signe(courant, "supprimer-compte")), motDePasse });
  await enregistrerEmail(null);
  return (await loadAccount())!;
}

/** Prendre (acheter) ou porter un article de la boutique. */
export async function boutique(courant: LocalAccount, action: "prendre" | "porter", article: string): Promise<Profil> {
  const r = await poster<{ profil: Profil }>("/api/comptes/boutique", {
    ...(await signe(courant, "boutique", action, article)),
    action,
    article,
  });
  return r.profil;
}

/** « Contester » une suspension : la réponse va à la régie. */
export async function contester(courant: LocalAccount, texte: string): Promise<void> {
  await poster("/api/comptes/contester", { ...(await signe(courant, "contester", texte)), texte });
}

/** Tout ce que le serveur garde sur ce compte (droit d'accès). */
export async function exporterCompte(courant: LocalAccount): Promise<unknown> {
  return poster("/api/comptes/export", await signe(courant, "exporter"));
}

/** Poser (ou retirer) la photo de profil du compte. */
export async function photoDuCompte(courant: LocalAccount, photo: string | null): Promise<void> {
  const e = photo ? await empreintePhoto(photo) : "aucune";
  await poster("/api/comptes/photo", { ...(await signe(courant, "photo", e)), photo });
}

/**
 * Signaler après coup quelqu'un d'une soirée passée : la salle, son propre
 * identifiant ce soir-là (la preuve d'y avoir été), les personnes visées.
 */
export async function signalerSalle(e: {
  sessionId: string;
  moi: string;
  cibles: string[];
  motif: string;
  texte: string;
}): Promise<void> {
  await poster("/api/signalements/salle", e);
}
